<div class="w-full mx-auto p-5 pb-20">

  <!-- === VIEW MODE === -->
  <div *ngIf="isViewMode && existingReconciliation" class="space-y-6 animate-fade-in-up mb-6">

    <!-- View Header -->
    <div class="bg-white p-6 rounded-lg shadow-sm flex items-center justify-between border-l-4" [ngClass]="{
           'border-green-500': existingReconciliation.status === 'COMPLETED',
           'border-yellow-500': existingReconciliation.status === 'IN_PROGRESS',
           'border-red-500': existingReconciliation.status === 'CANCELLED'
         }">
      <div>
        <div class="flex items-center gap-3 mb-1">
          <h1 class="text-2xl font-bold text-gray-800">Conciliación #{{ existingReconciliation.id }}</h1>
          <span class="px-3 py-1 rounded-full text-sm font-medium"
            [ngClass]="getStatusBadgeClass(existingReconciliation.status)">
            {{ getStatusDisplayName(existingReconciliation.status) }}
          </span>
        </div>
        <div class="text-gray-500 flex items-center gap-4 mt-2">
          <span class="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
            <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-5a2 2 0 012-2h2a2 2 0 012 2v5m-6 0V9a2 2 0 012-2h2a2 2 0 012 2v8">
              </path>
            </svg>
            {{ existingReconciliation.bank?.displayName || 'Banco' }}
          </span>
          <span class="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
            <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            {{ formatDate(existingReconciliation.periodFrom) }} - {{ formatDate(existingReconciliation.periodTo) }}
          </span>
        </div>
      </div>

      <div class="flex gap-3">
        <button (click)="goToReconciliationsList()"
          class="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200 font-medium flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18">
            </path>
          </svg>
          Volver
        </button>

        <button *ngIf="existingReconciliation.status === 'IN_PROGRESS'" (click)="completeReconciliation()"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium shadow-md flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Finalizar Conciliación
        </button>

        <button (click)="downloadReconciliationReport()" [disabled]="existingReconciliation.status !== 'COMPLETED'"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 font-medium shadow-md flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z">
            </path>
          </svg>
          Descargar Reporte
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Bank Amount -->
      <div class="bg-white p-6 rounded-lg shadow-sm border-t-4 border-blue-500">
        <div class="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Monto en Banco</div>
        <div class="text-3xl font-bold text-gray-800">{{ formatCurrency(existingReconciliation.amounts?.bankAmount || 0)
          }}</div>
      </div>

      <!-- Reconciled Amount -->
      <div class="bg-white p-6 rounded-lg shadow-sm border-t-4 border-green-500">
        <div class="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Monto Conciliado</div>
        <div class="text-3xl font-bold text-green-600">{{
          formatCurrency(existingReconciliation.amounts?.reconciledAmount || 0) }}</div>
        <div class="text-xs text-blue-600 mt-2 font-medium bg-blue-50 inline-block px-2 py-1 rounded">
          {{ existingReconciliation.counters?.reconciledCount || 0 }} comprobantes
        </div>
      </div>

      <!-- Difference -->
      <div class="bg-white p-6 rounded-lg shadow-sm border-t-4" [ngClass]="{
            'border-green-500': (existingReconciliation.amounts?.differenceAmount || 0) === 0,
            'border-red-500': (existingReconciliation.amounts?.differenceAmount || 0) !== 0
        }">
        <div class="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Diferencia</div>
        <div class="text-3xl font-bold" [ngClass]="{
                'text-green-600': (existingReconciliation.amounts?.differenceAmount || 0) === 0,
                'text-red-600': (existingReconciliation.amounts?.differenceAmount || 0) !== 0
            }">{{ formatCurrency(existingReconciliation.amounts?.differenceAmount || 0) }}</div>
        <p *ngIf="(existingReconciliation.amounts?.differenceAmount || 0) !== 0" class="text-red-500 text-xs mt-1">
          La conciliación presenta diferencias
        </p>
      </div>
    </div>

    <!-- Status Info (if Completed) -->
    <div *ngIf="existingReconciliation.status === 'COMPLETED'"
      class="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
      <h3 class="font-bold text-green-800 mb-3 flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Información de Cierre
      </h3>
      <div class="text-sm text-green-800 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex items-center gap-2">
          <span class="font-semibold">Cerrado por:</span>
          <span class="bg-white px-2 py-1 rounded shadow-sm">{{ existingReconciliation.updatedBy || 'Usuario' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">Fecha de cierre:</span>
          <span class="bg-white px-2 py-1 rounded shadow-sm">{{ existingReconciliation.updatedAt | date:'medium'
            }}</span>
        </div>
      </div>
    </div>

    <!-- Details Table -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
      <div class="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 class="font-bold text-gray-800 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01">
            </path>
          </svg>
          Detalle de Comprobantes Conciliados
        </h3>
        <span class="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">{{ existingReconciliation.details?.length
          || 0 }} registros</span>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 font-semibold">ID</th>
              <th class="px-6 py-3 font-semibold">Tipo</th>
              <th class="px-6 py-3 font-semibold">Referencia</th>
              <th class="px-6 py-3 font-semibold">Fecha Creación</th>
              <th class="px-6 py-3 font-semibold">Fecha Pago</th>
              <th class="px-6 py-3 font-semibold">Participante/Tercero</th>
              <th class="px-6 py-3 font-semibold">Concepto</th>
              <th class="px-6 py-3 text-right font-semibold">Monto Original</th>
              <th class="px-6 py-3 text-right font-semibold">Monto Conciliado</th>
              <th class="px-6 py-3 text-center font-semibold">Estado</th>
              <th class="px-6 py-3 text-center font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody *ngIf="existingReconciliation.details?.length > 0">
            <tr *ngFor="let detail of existingReconciliation.details"
              class="bg-white border-b hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 font-mono text-gray-800 font-semibold">{{ detail.receipt?.id }}</td>
              <td class="px-6 py-4">
                <span class="px-2 py-1 rounded text-xs font-semibold"
                  [ngClass]="detail.receiptType === 'INCOME' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                  {{ detail.receiptType === 'INCOME' ? 'Ingreso' : 'Egreso' }}
                </span>
              </td>
              <td class="px-6 py-4 font-mono text-gray-600 font-medium">{{ detail.receipt?.referenceNumber ||
                detail.receipt?.number || 'N/A' }}
              </td>
              <td class="px-6 py-4 text-gray-600">{{ detail.receipt?.createdAt | date:'dd/MM/yyyy HH:mm' }}</td>
              <td class="px-6 py-4 text-gray-600">{{ detail.receipt?.date | date:'dd/MM/yyyy' }}</td>
              <td class="px-6 py-4 text-gray-700">
                <span *ngIf="detail.receipt?.participantName">{{ detail.receipt.participantName }}</span>
                <span *ngIf="detail.receipt?.companyName">{{ detail.receipt.companyName }}</span>
                <span *ngIf="!detail.receipt?.participantName && !detail.receipt?.companyName"
                  class="text-gray-400">-</span>
              </td>
              <td class="px-6 py-4 text-gray-800">{{ detail.receipt?.description }}</td>
              <td class="px-6 py-4 text-right text-gray-600">{{ formatCurrency(detail.amounts?.originalAmount) }}</td>
              <td class="px-6 py-4 text-right font-bold text-gray-800">{{
                formatCurrency(detail.amounts?.reconciledAmount) }}</td>
              <td class="px-6 py-4 text-center">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"></path>
                  </svg>
                  Conciliado
                </span>
              </td>
              <td class="px-6 py-4 text-center">
                <button (click)="openVoucher(detail.receiptType, detail.receipt?.id)"
                  class="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm font-medium">
                  Ver <i class="pi pi-external-link text-xs"></i>
                </button>
              </td>
            </tr>
          </tbody>
          <tbody *ngIf="!existingReconciliation.details?.length">
            <tr>
              <td colspan="12" class="px-6 py-12 text-center text-gray-500">
                <div class="flex flex-col items-center justify-center">
                  <svg class="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01">
                    </path>
                  </svg>
                  <p>No hay detalles disponibles para mostrar.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <!-- Header -->
  <div *ngIf="!isViewMode" class="bg-white p-6 rounded-lg shadow-sm mb-6">
    <h1 class="text-2xl font-bold text-gray-800">Nueva Conciliación Bancaria</h1>
    <p class="text-gray-500 mt-1">Complete la información paso a paso para realizar la conciliación</p>
  </div>

  <!-- Barra de pasos para wizard -->
  <div *ngIf="!isViewMode" class="flex items-center justify-center mb-8">
    <div class="flex w-full max-w-6xl">
      <ng-container
        *ngFor="let label of ['Configuración', 'Cargar Comprobantes', 'Seleccionar', 'Conciliar', 'Finalizar']; let i = index">
        <div class="flex flex-1 text-center items-center">
          <div class="flex items-center justify-center w-10 h-10 rounded-full" [ngClass]="{
              'bg-blue-500 text-white': step === i + 1,
              'bg-green-500 text-white': step > i + 1,
              'bg-gray-300 text-gray-600': step < i + 1
            }">
            <span *ngIf="step <= i + 1">{{ i + 1 }}</span>
            <svg *ngIf="step > i + 1" class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="mx-2 flex-1">
            <span class="text-sm font-medium">{{ label }}</span>
          </div>
          <div *ngIf="i < (totalSteps - 1)" class="flex-1 h-0.5 bg-gray-300" [ngClass]="{'bg-blue-500': step > i + 1}">
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Formulario -->
  <form *ngIf="!isViewMode" [formGroup]="reconciliationForm" (ngSubmit)="onSubmit()">

    <!-- STEP 1: Configuración Básica -->
    <div *ngIf="step === 1" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Configuración Básica</h3>
      </div>

      <!-- Tipo de Conciliación -->
      <div class="mb-8">
        <h4 class="text-md font-semibold text-gray-800 mb-4">¿Qué tipo de conciliación desea realizar?</h4>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Opción: Solo Ingresos -->
          <div class="border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg" [ngClass]="{
                 'border-green-500 bg-green-50 shadow-lg': reconciliationType === ReconciliationType.INCOME,
                 'border-gray-200 hover:border-gray-300': reconciliationType !== ReconciliationType.INCOME
               }" (click)="onReconciliationTypeChange(ReconciliationType.INCOME)">

            <div class="flex items-center mb-4">
              <input type="radio" [value]="ReconciliationType.INCOME"
                [checked]="reconciliationType === ReconciliationType.INCOME"
                (change)="onReconciliationTypeChange(ReconciliationType.INCOME)"
                class="text-green-600 focus:ring-green-500 mr-3">
              <div class="text-2xl mr-3">💰</div>
              <div>
                <h5 class="text-lg font-bold text-gray-800">Solo Ingresos</h5>
                <span class="text-sm text-green-600 font-medium">Comprobantes de ingreso</span>
              </div>
            </div>

            <div class="space-y-3 text-sm text-gray-700">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Conciliar solo pagos recibidos</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Ideal para ingresos por matrículas</span>
              </div>
            </div>
          </div>

          <!-- Opción: Solo Egresos -->
          <div class="border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg" [ngClass]="{
                 'border-red-500 bg-red-50 shadow-lg': reconciliationType === ReconciliationType.EXPENSE,
                 'border-gray-200 hover:border-gray-300': reconciliationType !== ReconciliationType.EXPENSE
               }" (click)="onReconciliationTypeChange(ReconciliationType.EXPENSE)">

            <div class="flex items-center mb-4">
              <input type="radio" [value]="ReconciliationType.EXPENSE"
                [checked]="reconciliationType === ReconciliationType.EXPENSE"
                (change)="onReconciliationTypeChange(ReconciliationType.EXPENSE)"
                class="text-red-600 focus:ring-red-500 mr-3">
              <div class="text-2xl mr-3">💸</div>
              <div>
                <h5 class="text-lg font-bold text-gray-800">Solo Egresos</h5>
                <span class="text-sm text-red-600 font-medium">Comprobantes de egreso</span>
              </div>
            </div>

            <div class="space-y-3 text-sm text-gray-700">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Conciliar solo pagos realizados</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Ideal para pagos a proveedores</span>
              </div>
            </div>
          </div>

          <!-- Opción: Conciliación Completa -->
          <div class="border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg" [ngClass]="{
                 'border-purple-500 bg-purple-50 shadow-lg': reconciliationType === ReconciliationType.BOTH,
                 'border-gray-200 hover:border-gray-300': reconciliationType !== ReconciliationType.BOTH
               }" (click)="onReconciliationTypeChange(ReconciliationType.BOTH)">

            <div class="flex items-center mb-4">
              <input type="radio" [value]="ReconciliationType.BOTH"
                [checked]="reconciliationType === ReconciliationType.BOTH"
                (change)="onReconciliationTypeChange(ReconciliationType.BOTH)"
                class="text-purple-600 focus:ring-purple-500 mr-3">
              <div class="text-2xl mr-3">🔄</div>
              <div>
                <h5 class="text-lg font-bold text-gray-800">Conciliación Completa</h5>
                <span class="text-sm text-purple-600 font-medium">Ingresos y egresos</span>
              </div>
            </div>

            <div class="space-y-3 text-sm text-gray-700">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-purple-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Conciliar todos los movimientos</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-purple-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Visión completa del período</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Campos de configuración -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Banco -->
        <div>
          <label for="bankId" class="block text-gray-700 font-bold mb-2" appRequiredLabel>🏦 Banco</label>
          <p-dropdown id="bankId" formControlName="bankId" [filter]="true" filterBy="label" [showClear]="true"
            [options]="banks" placeholder="Seleccione un banco" (onChange)="onBankChange()"
            class="w-full custom-dropdown">
          </p-dropdown>
          <div *ngIf="reconciliationForm.get('bankId')?.invalid && reconciliationForm.get('bankId')?.touched"
            class="text-red-500 text-sm">
            Banco es obligatorio.
          </div>
        </div>

        <!-- Monto Total del Banco -->
        <div>
          <label for="totalBankAmount" class="block text-gray-700 font-bold mb-2" appRequiredLabel>💵 Monto Total del
            Banco</label>
          <input type="text" appCurrencyFormatter formControlName="totalBankAmount"
            class="w-full px-3 py-2 border rounded-md" placeholder="Ingrese el monto reportado por el banco" />
          <div
            *ngIf="reconciliationForm.get('totalBankAmount')?.invalid && reconciliationForm.get('totalBankAmount')?.touched"
            class="text-red-500 text-sm">
            Monto del banco es obligatorio y debe ser mayor a cero.
          </div>
          <p class="text-xs text-gray-500 mt-1">Monto total reportado por el banco para el período</p>
        </div>

        <!-- Mes -->
        <div>
          <label for="month" class="block text-gray-700 font-bold mb-2" appRequiredLabel>📅 Mes a Conciliar</label>
          <p-dropdown id="month" formControlName="month" [options]="getMonthOptions()" placeholder="Seleccione un mes"
            optionLabel="label" optionValue="value" (onChange)="onMonthYearChange()" class="w-full custom-dropdown">
          </p-dropdown>
          <div *ngIf="reconciliationForm.get('month')?.invalid && reconciliationForm.get('month')?.touched"
            class="text-red-500 text-sm">
            Mes es obligatorio.
          </div>
        </div>

        <!-- Año -->
        <div>
          <label for="year" class="block text-gray-700 font-bold mb-2" appRequiredLabel>📅 Año a Conciliar</label>
          <p-dropdown id="year" formControlName="year" [options]="getYearOptions()" placeholder="Seleccione un año"
            optionLabel="label" optionValue="value" (onChange)="onMonthYearChange()" class="w-full custom-dropdown">
          </p-dropdown>
          <div *ngIf="reconciliationForm.get('year')?.invalid && reconciliationForm.get('year')?.touched"
            class="text-red-500 text-sm">
            Año es obligatorio.
          </div>
          <div
            *ngIf="!isValidPeriod() && reconciliationForm.get('month')?.value && reconciliationForm.get('year')?.value"
            class="text-red-500 text-sm">
            El mes/año seleccionado no es válido o es futuro.
          </div>
        </div>
      </div>

      <!-- Filtros Opcionales -->
      <div class="mt-8">
        <h4 class="text-md font-semibold text-gray-800 mb-4">🔍 Filtros Opcionales</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Proyecto -->
          <div>
            <label for="projectId" class="block text-gray-700 font-bold mb-2">Proyecto</label>
            <p-dropdown id="projectId" formControlName="projectId" [filter]="true" filterBy="label" [showClear]="true"
              [options]="projects" placeholder="Filtrar por proyecto" class="w-full custom-dropdown">
            </p-dropdown>
          </div>

          <!-- Participante -->
          <div>
            <label for="participantId" class="block text-gray-700 font-bold mb-2">Participante</label>
            <p-dropdown id="participantId" formControlName="participantId" [filter]="true" filterBy="label"
              [showClear]="true" [options]="participants" placeholder="Filtrar por participante"
              class="w-full custom-dropdown">
            </p-dropdown>
          </div>

          <!-- Empresa -->
          <div>
            <label for="companyId" class="block text-gray-700 font-bold mb-2">Empresa</label>
            <p-dropdown id="companyId" formControlName="companyId" [filter]="true" filterBy="label" [showClear]="true"
              [options]="companies" placeholder="Filtrar por empresa" class="w-full custom-dropdown">
            </p-dropdown>
          </div>

          <!-- Monto Mínimo -->
          <div>
            <label for="minAmount" class="block text-gray-700 font-bold mb-2">Monto Mínimo</label>
            <input type="text" appCurrencyFormatter formControlName="minAmount"
              class="w-full px-3 py-2 border rounded-md" placeholder="Monto mínimo opcional" />
          </div>

          <!-- Monto Máximo -->
          <div>
            <label for="maxAmount" class="block text-gray-700 font-bold mb-2">Monto Máximo</label>
            <input type="text" appCurrencyFormatter formControlName="maxAmount"
              class="w-full px-3 py-2 border rounded-md" placeholder="Monto máximo opcional" />
          </div>

          <!-- Tolerancia para Auto-conciliación -->
          <div>
            <label for="autoReconciliationTolerance" class="block text-gray-700 font-bold mb-2">Tolerancia ($)</label>
            <input type="number" step="0.01" min="0.001" max="100" formControlName="autoReconciliationTolerance"
              class="w-full px-3 py-2 border rounded-md" />
            <p class="text-xs text-gray-500 mt-1">Diferencia permitida para conciliación automática</p>
          </div>
        </div>
      </div>

      <!-- Notas -->
      <div class="mt-6">
        <label for="notes" class="block text-gray-700 font-bold mb-2">📝 Notas</label>
        <textarea formControlName="notes" class="w-full px-3 py-2 border rounded-md resize-none" rows="3"
          placeholder="Notas y observaciones sobre la conciliación (opcional)"></textarea>
      </div>
    </div>

    <!-- STEP 2: Cargar Comprobantes -->
    <div *ngIf="step === 2" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Cargar Comprobantes Pendientes</h3>
      </div>

      <!-- Resumen de configuración -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mb-8">
        <h4 class="text-lg font-bold text-gray-800 mb-4">📋 Configuración Seleccionada</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Banco</div>
            <div class="text-lg font-bold text-gray-800">{{ getSelectedBankName() }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Tipo</div>
            <div class="text-lg font-bold text-gray-800">{{ getReconciliationTypeDisplayName() }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Período</div>
            <div class="text-lg font-bold text-gray-800">
              {{ getSelectedPeriodDisplay() }}
            </div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Monto Banco</div>
            <div class="text-lg font-bold text-green-600">{{
              formatCurrency(reconciliationForm.get('totalBankAmount')?.value || 0) }}</div>
          </div>
        </div>
      </div>

      <!-- Botón para cargar comprobantes -->
      <div class="text-center">
        <button type="button" (click)="loadPendingReceipts()" [disabled]="!isStep1Valid()"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 flex items-center gap-3 mx-auto shadow-lg">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
          </svg>
          <span class="text-lg">Cargar Comprobantes Pendientes</span>
        </button>
        <p class="text-gray-600 mt-3">Se cargarán todos los comprobantes pendientes de conciliar para el período
          seleccionado</p>
      </div>
    </div>

    <!-- STEP 3: Selección de Comprobantes -->
    <div *ngIf="step === 3" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Seleccionar Comprobantes a Conciliar</h3>
      </div>

      <!-- 🆕 CONTROLES MEJORADOS DE SELECCIÓN Y CONFIGURACIÓN -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mb-6">

        <!-- Controles principales -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div class="flex flex-wrap gap-3">
            <!-- Selección -->
            <button type="button" (click)="selectAllReceipts()"
              class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 transform hover:scale-105">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"></path>
              </svg>
              Seleccionar Todos
            </button>

            <button type="button" (click)="clearAllSelections()"
              class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 transform hover:scale-105">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              Limpiar Selección
            </button>

            <!-- 🆕 Configuración por Lotes -->
            <button type="button" (click)="openBatchConfigModal()" [disabled]="selectedReceipts.length === 0"
              class="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 transform hover:scale-105 disabled:scale-100">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              🚀 Configurar por Lotes
            </button>
          </div>
        </div>

        <!-- Estadísticas de selección y configuración -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white p-3 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-1">Seleccionados</div>
            <div class="text-xl font-bold text-blue-600">{{ selectedReceipts.length }}</div>
          </div>
          <div class="bg-white p-3 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-1">Configurados</div>
            <div class="text-xl font-bold text-green-600">{{ getConfiguredReceiptsCount() }}</div>
          </div>
          <div class="bg-white p-3 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-1">Balanceados</div>
            <div class="text-xl font-bold text-emerald-600">{{ getBalancedReceiptsCount() }}</div>
          </div>
          <div class="bg-white p-3 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-1">Progreso</div>
            <div class="text-xl font-bold text-purple-600">
              {{ selectedReceipts.length > 0 ? ((getConfiguredReceiptsCount() / selectedReceipts.length) *
              100).toFixed(0) : 0 }}%
            </div>
          </div>
        </div>

        <!-- Barra de progreso -->
        <div class="mt-4">
          <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progreso de Configuración</span>
            <span>{{ getConfiguredReceiptsCount() }} de {{ selectedReceipts.length }}</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
              [style.width.%]="selectedReceipts.length > 0 ? (getConfiguredReceiptsCount() / selectedReceipts.length) * 100 : 0">
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de Comprobantes de Ingreso -->
      <div *ngIf="pendingIncomeReceipts.length > 0" class="mb-8">
        <h4 class="text-lg font-semibold text-green-800 mb-4 flex items-center">
          <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          💰 Comprobantes de Ingreso ({{ pendingIncomeReceipts.length }})
        </h4>

        <div class="overflow-x-auto">
          <table class="w-full border border-gray-200 rounded-lg">
            <thead class="bg-green-50">
              <tr>
                <th class="p-3 text-left">Seleccionar</th>
                <th class="p-3 text-left">Estado</th>
                <th class="p-3 text-left">Tipo</th>
                <th class="p-3 text-left">ID</th>
                <th class="p-3 text-left">Referencia</th>
                <th class="p-3 text-left">Fecha</th>
                <th class="p-3 text-left">Concepto</th>
                <th class="p-3 text-left">Pagador</th>
                <th class="p-3 text-right">Monto Neto</th>
                <th class="p-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let receipt of pendingIncomeReceipts; let i = index"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200" [ngClass]="{
                    'bg-green-50 border-green-200': isReceiptSelected(receipt, 'income'),
                    'bg-white': !isReceiptSelected(receipt, 'income')
                  }">
                <td class="p-3">
                  <input type="checkbox" [checked]="isReceiptSelected(receipt, 'income')"
                    (change)="toggleReceiptSelection(receipt, 'income')"
                    class="text-green-600 focus:ring-green-500 w-4 h-4">
                </td>
                <td class="p-3">
                  <div *ngIf="isReceiptSelected(receipt, 'income')" class="flex items-center space-x-2">
                    <span class="text-lg">{{ getReceiptStatusIconForTable(receipt, 'income') }}</span>
                    <span class="text-xs font-medium" [ngClass]="getReceiptStatusColorForTable(receipt, 'income')">
                      {{ getReceiptStatusTextForTable(receipt, 'income') }}
                    </span>
                  </div>
                </td>
                <td class="p-3">
                  <div class="flex items-center space-x-2">
                    <span class="text-xl">💰</span>
                    <span class="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">INGRESO</span>
                  </div>
                </td>
                <td class="p-3">
                  <a [href]="'/income-receipts/view/' + receipt.id" target="_blank"
                    class="text-blue-600 hover:text-blue-800 hover:underline font-mono text-sm font-semibold"
                    title="Ver comprobante en nueva pestaña">
                    IR-{{ receipt.id }}
                    <svg class="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z">
                      </path>
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z">
                      </path>
                    </svg>
                  </a>
                </td>
                <td class="p-3 font-mono text-sm">{{ receipt.referenceNumber || 'N/A' }}</td>
                <td class="p-3">{{ receipt.transactionDate | date:'dd/MM/yyyy' }}</td>
                <td class="p-3">{{ receipt.concept }}</td>
                <td class="p-3">{{ receipt.payerDescription }}</td>
                <td class="p-3 text-right font-semibold text-green-600">{{ formatCurrency(receipt.netAmount) }}</td>
                <td class="p-3 text-center">
                  <button *ngIf="isReceiptSelected(receipt, 'income')" type="button"
                    (click)="openReceiptConfigModalForTable(receipt, 'income')"
                    class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors duration-200">
                    ⚙️ Configurar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Lista de Comprobantes de Egreso -->
      <div *ngIf="pendingExpenseVouchers.length > 0" class="mb-8">
        <h4 class="text-lg font-semibold text-red-800 mb-4 flex items-center">
          <div class="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          💸 Comprobantes de Egreso ({{ pendingExpenseVouchers.length }})
        </h4>

        <div class="overflow-x-auto">
          <table class="w-full border border-gray-200 rounded-lg">
            <thead class="bg-red-50">
              <tr>
                <th class="p-3 text-left">Seleccionar</th>
                <th class="p-3 text-left">Estado</th>
                <th class="p-3 text-left">Tipo</th>
                <th class="p-3 text-left">ID</th>
                <th class="p-3 text-left">Fecha Pago</th>
                <th class="p-3 text-left">Concepto</th>
                <th class="p-3 text-left">Beneficiario</th>
                <th class="p-3 text-right">Monto a Pagar</th>
                <th class="p-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let receipt of pendingExpenseVouchers; let i = index"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200" [ngClass]="{
                    'bg-red-50 border-red-200': isReceiptSelected(receipt, 'expense'),
                    'bg-white': !isReceiptSelected(receipt, 'expense')
                  }">
                <td class="p-3">
                  <input type="checkbox" [checked]="isReceiptSelected(receipt, 'expense')"
                    (change)="toggleReceiptSelection(receipt, 'expense')"
                    class="text-red-600 focus:ring-red-500 w-4 h-4">
                </td>
                <td class="p-3">
                  <div *ngIf="isReceiptSelected(receipt, 'expense')" class="flex items-center space-x-2">
                    <span class="text-lg">{{ getReceiptStatusIconForTable(receipt, 'expense') }}</span>
                    <span class="text-xs font-medium" [ngClass]="getReceiptStatusColorForTable(receipt, 'expense')">
                      {{ getReceiptStatusTextForTable(receipt, 'expense') }}
                    </span>
                  </div>
                </td>
                <td class="p-3">
                  <div class="flex items-center space-x-2">
                    <span class="text-xl">💸</span>
                    <span class="text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded">EGRESO</span>
                  </div>
                </td>
                <td class="p-3">
                  <a [href]="'/expense-vouchers/view/' + receipt.id" target="_blank"
                    class="text-blue-600 hover:text-blue-800 hover:underline font-mono text-sm font-semibold"
                    title="Ver comprobante en nueva pestaña">
                    EV-{{ receipt.id }}
                    <svg class="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z">
                      </path>
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z">
                      </path>
                    </svg>
                  </a>
                </td>
                <td class="p-3">{{ receipt.paymentDate | date:'dd/MM/yyyy' }}</td>
                <td class="p-3">{{ receipt.concept }}</td>
                <td class="p-3">{{ receipt.beneficiaryDescription }}</td>
                <td class="p-3 text-right font-semibold text-red-600">{{ formatCurrency(receipt.totalToPay) }}</td>
                <td class="p-3 text-center">
                  <button *ngIf="isReceiptSelected(receipt, 'expense')" type="button"
                    (click)="openReceiptConfigModalForTable(receipt, 'expense')"
                    class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors duration-200">
                    ⚙️ Configurar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 🆕 RESUMEN COMPACTO DE COMPROBANTES SELECCIONADOS -->
      <div *ngIf="selectedReceipts.length > 0" class="space-y-6">

        <!-- Resumen de Selección -->
        <div class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-lg font-bold text-gray-800">📊 Resumen de Conciliación</h4>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600">Estado:</span>
              <span class="px-3 py-1 rounded-full text-sm font-medium" [ngClass]="{
                      'bg-green-100 text-green-800': getAllReceiptsConfigured(),
                      'bg-yellow-100 text-yellow-800': !getAllReceiptsConfigured() && getConfiguredReceiptsCount() > 0,
                      'bg-gray-100 text-gray-800': getConfiguredReceiptsCount() === 0
                    }">
                {{ getAllReceiptsConfigured() ? '✅ Todos Configurados' :
                getConfiguredReceiptsCount() > 0 ? '⚠️ Configuración Parcial' : '⏳ Sin Configurar' }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="bg-white p-4 rounded-lg border shadow-sm">
              <div class="text-sm font-medium text-gray-600 mb-2">Comprobantes</div>
              <div class="text-xl font-bold text-blue-600">{{ reconciliationSummary.selectedCount }}</div>
            </div>
            <div class="bg-white p-4 rounded-lg border shadow-sm">
              <div class="text-sm font-medium text-gray-600 mb-2">Total Seleccionado</div>
              <div class="text-xl font-bold text-green-600">{{ formatCurrency(reconciliationSummary.totalSelected) }}
              </div>
            </div>
            <div class="bg-white p-4 rounded-lg border shadow-sm">
              <div class="text-sm font-medium text-gray-600 mb-2">Monto Banco</div>
              <div class="text-xl font-bold text-blue-600">{{ formatCurrency(reconciliationSummary.totalBank) }}</div>
            </div>
            <div class="bg-white p-4 rounded-lg border shadow-sm">
              <div class="text-sm font-medium text-gray-600 mb-2">Diferencia</div>
              <div class="text-xl font-bold" [ngClass]="{
                     'text-green-600': Math.abs(reconciliationSummary.differenceAmount) <= 0.01,
                     'text-yellow-600': Math.abs(reconciliationSummary.differenceAmount) > 0.01 && Math.abs(reconciliationSummary.differenceAmount) <= 100,
                     'text-red-600': Math.abs(reconciliationSummary.differenceAmount) > 100
                   }">
                {{ formatCurrency(reconciliationSummary.differenceAmount) }}
              </div>
            </div>
          </div>
        </div>

        <!-- 🆕 INSTRUCCIONES PARA LA NUEVA UX -->
        <div class="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border border-purple-200">
          <div class="flex items-start space-x-4">
            <div class="flex-shrink-0">
              <svg class="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="text-lg font-bold text-purple-800 mb-2">🚀 ¡Nueva Experiencia de Configuración!</h4>
              <div class="text-purple-700 space-y-2">
                <p class="font-medium">Para configurar los comprobantes seleccionados, tienes dos opciones:</p>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div class="bg-white p-4 rounded-lg border border-purple-200">
                    <div class="flex items-center space-x-2 mb-2">
                      <span class="text-xl">🚀</span>
                      <span class="font-semibold text-purple-800">Configuración por Lotes</span>
                    </div>
                    <p class="text-sm text-purple-600">Aplica la misma configuración a múltiples comprobantes de una
                      vez. Ideal para volúmenes altos.</p>
                  </div>
                  <div class="bg-white p-4 rounded-lg border border-purple-200">
                    <div class="flex items-center space-x-2 mb-2">
                      <span class="text-xl">⚙️</span>
                      <span class="font-semibold text-purple-800">Configuración Individual</span>
                    </div>
                    <p class="text-sm text-purple-600">Configura cada comprobante por separado usando el botón
                      "Configurar" en la tabla.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 4: Conciliación Automática -->
    <div *ngIf="step === 4" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Conciliación Automática</h3>
      </div>

      <!-- Información previa -->
      <div class="bg-yellow-50 p-6 rounded-lg border border-yellow-200 mb-6">
        <div class="flex items-start">
          <svg class="w-6 h-6 text-yellow-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"></path>
          </svg>
          <div>
            <h4 class="font-semibold text-yellow-800 mb-2">🤖 Proceso de Conciliación Automática</h4>
            <p class="text-sm text-yellow-700 mb-3">
              El sistema intentará conciliar automáticamente los comprobantes seleccionados
              basándose en la coincidencia de montos con la tolerancia configurada.
            </p>
            <div class="bg-white p-3 rounded border">
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="font-medium text-gray-600">Comprobantes a procesar:</span>
                  <span class="ml-2 font-bold">{{ reconciliationSummary.selectedCount }}</span>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Tolerancia configurada:</span>
                  <span class="ml-2 font-bold">{{
                    formatCurrency(reconciliationForm.get('autoReconciliationTolerance')?.value || 0) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Botón para iniciar conciliación automática -->
      <div class="text-center">
        <button type="button" (click)="performAutoReconciliation()" [disabled]="!isStep3Valid()"
          class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 flex items-center gap-3 mx-auto shadow-lg">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
          <span class="text-lg">Iniciar Conciliación Automática</span>
        </button>
      </div>

      <!-- Resultados de conciliación automática -->
      <div *ngIf="showAutoReconcileResults && autoReconcileResults"
        class="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
        <h4 class="text-lg font-bold text-green-800 mb-4">✅ Resultados de Conciliación Automática</h4>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Procesados</div>
            <div class="text-xl font-bold text-blue-600">{{ autoReconcileResults.processedCount }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Conciliados</div>
            <div class="text-xl font-bold text-green-600">{{ autoReconcileResults.successfullyReconciled }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Con Diferencias</div>
            <div class="text-xl font-bold text-yellow-600">{{ autoReconcileResults.withDifferences }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="text-sm font-medium text-gray-600 mb-2">Fallidos</div>
            <div class="text-xl font-bold text-red-600">{{ autoReconcileResults.failed }}</div>
          </div>
        </div>

        <!-- Monto conciliado -->
        <div class="bg-white p-4 rounded-lg border">
          <div class="flex justify-between items-center">
            <span class="font-medium text-gray-700">Monto Total Conciliado:</span>
            <span class="text-xl font-bold text-green-600">{{ formatCurrency(autoReconcileResults.totalReconciledAmount)
              }}</span>
          </div>
          <div class="flex justify-between items-center mt-2" *ngIf="autoReconcileResults.totalDifferencesAmount > 0">
            <span class="font-medium text-gray-700">Total de Diferencias:</span>
            <span class="text-lg font-bold text-red-600">{{ formatCurrency(autoReconcileResults.totalDifferencesAmount)
              }}</span>
          </div>
        </div>

        <!-- Mensajes de resultados -->
        <div *ngIf="autoReconcileResults.messages?.length > 0" class="mt-4">
          <h5 class="font-semibold text-gray-800 mb-2">📝 Detalles:</h5>
          <div class="bg-white p-3 rounded border max-h-32 overflow-y-auto">
            <div *ngFor="let message of autoReconcileResults.messages" class="text-sm text-gray-700 mb-1">
              • {{ message }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 5: Finalización y Éxito -->
    <div *ngIf="step === 5" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-green-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">¡Conciliación Finalizada!</h3>
      </div>

      <!-- Mensaje de éxito -->
      <div class="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 mb-6">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0">
            <svg class="w-12 h-12 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-4">
            <h4 class="text-xl font-bold text-green-800 mb-2">🎉 ¡Conciliación Completada!</h4>
            <p class="text-green-700">
              La conciliación bancaria se ha procesado exitosamente. Todos los comprobantes
              han sido marcados como conciliados según corresponda.
            </p>
          </div>
        </div>
      </div>

      <!-- Resumen Final -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Información de la conciliación -->
        <div class="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h5 class="font-semibold text-blue-800 mb-4">📋 Información de la Conciliación</h5>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">ID:</span>
              <span class="font-semibold">{{ completedReconciliation?.id }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Banco:</span>
              <span class="font-semibold">{{ getSelectedBankName() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Tipo:</span>
              <span class="font-semibold">{{ getReconciliationTypeDisplayName() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Estado:</span>
              <span class="font-semibold text-green-600">{{ isReconciliationCompleted ? 'Completada' : 'En Progreso'
                }}</span>
            </div>
          </div>
        </div>

        <!-- Resumen de Montos -->
        <div class="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h5 class="font-semibold text-gray-800 mb-4">💰 Resumen de Montos</h5>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Monto Banco:</span>
              <span class="font-semibold">{{ formatCurrency(reconciliationSummary.totalBank) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Total Conciliado:</span>
              <span class="font-semibold text-green-600">{{ formatCurrency(autoReconcileResults?.totalReconciledAmount
                || 0) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Diferencias:</span>
              <span class="font-semibold text-red-600">{{ formatCurrency(autoReconcileResults?.totalDifferencesAmount ||
                0) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">% Conciliado:</span>
              <span class="font-semibold text-blue-600">{{ (autoReconcileResults?.successfullyReconciled /
                autoReconcileResults?.processedCount * 100 || 0).toFixed(1) }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Acciones finales -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button type="button" (click)="downloadReconciliationReport()"
          class="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z">
            </path>
          </svg>
          Descargar Reporte Excel
        </button>

        <button type="button" (click)="createAnotherReconciliation()"
          class="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clip-rule="evenodd"></path>
          </svg>
          Nueva Conciliación
        </button>
      </div>
    </div>

    <!-- Navegación del wizard -->
    <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">

      <!-- Botón Atrás -->
      <button *ngIf="step > 1 && step <= 4" type="button" (click)="goToStep(step - 1)"
        class="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"></path>
        </svg>
        Atrás
      </button>

      <!-- Espaciador cuando no hay botón atrás -->
      <div *ngIf="step === 1 || step === 5"></div>

      <!-- Botón Siguiente (Step 1) -->
      <button *ngIf="step === 1" type="button" [disabled]="!isCurrentStepValid()" (click)="goToStep(2)"
        class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        Cargar Comprobantes
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd"></path>
        </svg>
      </button>

      <!-- Botón Siguiente (Step 3) -->
      <button *ngIf="step === 3" type="button" [disabled]="!isCurrentStepValid()" (click)="goToStep(4)"
        class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        Continuar a Conciliación
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd"></path>
        </svg>
      </button>

      <!-- Acciones finales (Step 5) -->
      <div *ngIf="step === 5" class="flex gap-4">
        <button type="button" (click)="goToReconciliationsList()"
          class="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clip-rule="evenodd"></path>
          </svg>
          Ver Lista de Conciliaciones
        </button>
      </div>

    </div>
  </form>
</div>

<!-- 🆕 Modal de Configuración Individual -->
<app-receipt-config-modal [isVisible]="showReceiptConfigModal" [receipt]="currentReceiptForModal"
  [receiptIndex]="currentReceiptIndex" [totalReceipts]="selectedReceipts.length" [feeTypes]="feeTypes"
  [receiptFormGroup]="getCurrentReceiptFormGroup()" (close)="closeReceiptConfigModal()"
  (save)="onReceiptConfigSave($event)" (navigateToReceipt)="navigateToReceiptInModal($event)"
  (feeChanged)="onReceiptFeeChanged($event)">
</app-receipt-config-modal>

<!-- 🆕 Modal de Configuración por Lotes -->
<app-batch-config-modal [isVisible]="showBatchConfigModal" [selectedReceipts]="selectedReceipts" [feeTypes]="feeTypes"
  (close)="closeBatchConfigModal()" (apply)="onBatchConfigApply($event)">
</app-batch-config-modal>

<!-- Modal de Confirmación -->
<app-confirmation-modal #confirmationModal [isVisible]="showConfirmationModal" [config]="confirmationConfig"
  (confirmed)="onConfirmationModalConfirmed()" (cancelled)="onConfirmationModalCancelled()"
  (closed)="onConfirmationModalClosed()">
</app-confirmation-modal>